// utils/mediaUrl.js
//
// Turning what is stored into something a browser can actually load.
//
// Two kinds of value in these columns are not web URLs, and both of them are
// the app's doing:
//
// 1. `assets_images_3` — a token for an image bundled INSIDE the app. The app
//    resolves it against its own bundle; a browser has no such bundle, so the
//    string went straight into `<img src>` and resolved against the site's own
//    origin, giving a 404 for https://invits.co/assets_images_3. The site
//    ships the same numbered pictures in /images/backgrounds, so the token can
//    simply be mapped.
//
// 2. `http://192.168.1.18:8081/assets/?unstable_path=...` — a Metro dev-server
//    URL, written by a development build of the app and now permanently in the
//    row. It cannot load from anywhere else, and because the site is HTTPS the
//    browser blocks it as mixed content before it even tries. There is nothing
//    to recover here; the right answer is to treat it as "no image" so the
//    placeholder shows instead of a broken one.
//
// Everything else passes through untouched.

/** `assets_images_3` and friends → a picture the website actually serves. */
const ASSET_TOKEN = /^assets?[_/]images?[_/](\d+)$/i;

/** How many of /images/backgrounds/N.jpg exist. */
const BUNDLED_COUNT = 8;

/**
 * Hosts nothing outside the developer's own network can reach.
 *
 * Metro serves on :8081 over plain HTTP at whatever the machine's LAN address
 * happens to be. Matching the private ranges and localhost catches every
 * variant of that without a list of individual addresses.
 */
const UNREACHABLE_HOST = /^https?:\/\/(localhost|127\.|0\.0\.0\.0|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/i;

/**
 * @param {unknown} value a stored media URL, token, or nothing
 * @returns {string|null} something loadable, or null to fall back
 */
export function resolveMediaUrl(value) {
  if (typeof value !== 'string') return null;

  const raw = value.trim();
  if (!raw) return null;

  const token = raw.match(ASSET_TOKEN);
  if (token) {
    // The numbering starts at 1 and the app has more of these than the site
    // does, so it wraps rather than 404ing on a high number.
    const n = ((parseInt(token[1], 10) - 1) % BUNDLED_COUNT + BUNDLED_COUNT) % BUNDLED_COUNT;
    return `/images/backgrounds/${n + 1}.jpg`;
  }

  if (UNREACHABLE_HOST.test(raw)) return null;

  // A bare filename or a path with no scheme is the app's too, and would
  // resolve against this origin and 404.
  if (!/^(https?:|data:|blob:|\/)/i.test(raw)) return null;

  return raw;
}

/** The first of several candidates that resolves to something loadable. */
export function firstMediaUrl(...values) {
  for (const value of values) {
    const resolved = resolveMediaUrl(value);
    if (resolved) return resolved;
  }
  return null;
}
